<script>
import { mapGetters } from 'vuex';
import { useAlert, useTrack } from 'dashboard/composables';
import CsatMetrics from './components/CsatMetrics.vue';
import CsatTable from './components/CsatTable.vue';
import CsatFilters from './components/Csat/CsatFilters.vue';
import CsatAgentRanking from './components/Csat/CsatAgentRanking.vue';
import CsatAlertSettingsDialog from './components/Csat/CsatAlertSettingsDialog.vue';
import CsatSynthesisDialog from './components/Csat/CsatSynthesisDialog.vue';
import ReportsAPI from '../../../../api/reports';
import { generateFileName } from '../../../../helper/downloadHelper';
import { REPORTS_EVENTS } from '../../../../helper/AnalyticsHelper/events';
import { FEATURE_FLAGS } from '../../../../featureFlags';
import V4Button from 'dashboard/components-next/button/Button.vue';
import ReportHeader from './components/ReportHeader.vue';

export default {
  name: 'CsatResponses',
  components: {
    CsatMetrics,
    CsatTable,
    CsatFilters,
    CsatAgentRanking,
    CsatAlertSettingsDialog,
    CsatSynthesisDialog,
    ReportHeader,
    V4Button,
  },
  data() {
    return {
      pageIndex: 0,
      from: 0,
      to: 0,
      userIds: [],
      inbox: null,
      team: null,
      rating: null,
      isCsatSynthesisLoading: false,
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      isFeatureEnabledOnAccount: 'accounts/isFeatureEnabledonAccount',
    }),
    requestPayload() {
      return {
        from: this.from,
        to: this.to,
        user_ids: this.userIds,
        inbox_id: this.inbox,
        team_id: this.team,
        rating: this.rating,
      };
    },
    isTeamsEnabled() {
      return this.isFeatureEnabledOnAccount(
        this.accountId,
        FEATURE_FLAGS.TEAM_MANAGEMENT
      );
    },
  },
  methods: {
    getAllData() {
      try {
        this.$store.dispatch('csat/getMetrics', this.requestPayload);
        this.$store.dispatch('csat/getAgentRanking', this.requestPayload);
        this.getResponses();
      } catch {
        useAlert(this.$t('REPORT.DATA_FETCHING_FAILED'));
      }
    },
    openAlertSettings() {
      this.$refs.alertSettingsDialog.open();
    },
    openSynthesisDialog() {
      this.$refs.csatSynthesisDialog.open();
    },
    async onCsatSynthesizeConfirm(payload) {
      if (this.isCsatSynthesisLoading) {
        return;
      }

      this.isCsatSynthesisLoading = true;
      try {
        const response = await ReportsAPI.synthesizeCsatResponses({
          from: payload.from,
          to: payload.to,
          agentIds: payload.agentIds,
          contactIds: payload.contactIds,
          useDateRange: payload.useDateRange,
          agentContactPair: payload.agentContactPair,
          questions: payload.questions || [],
        });

        const blob = new Blob([response.data], {
          type: response.headers?.['content-type'] || 'text/plain',
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `sintese-csat-${new Date()
          .toISOString()
          .slice(0, 19)
          .replace(/[T:]/g, '-')}.txt`;
        link.click();
        window.URL.revokeObjectURL(url);
        this.$refs.csatSynthesisDialog?.closeSynthesis?.();
        this.$refs.csatSynthesisDialog?.close?.();
      } catch (error) {
        const message =
          error.response?.status === 404
            ? this.$t('CSAT_SYNTHESIS.ERRORS.NO_RESULTS')
            : this.$t('CSAT_SYNTHESIS.ERRORS.GENERIC');
        useAlert(message);
      } finally {
        this.isCsatSynthesisLoading = false;
      }
    },
    getResponses() {
      this.$store.dispatch('csat/get', {
        page: this.pageIndex + 1,
        ...this.requestPayload,
      });
    },
    downloadReports() {
      const type = 'csat';
      try {
        this.$store.dispatch('csat/downloadCSATReports', {
          fileName: generateFileName({ type, to: this.to }),
          ...this.requestPayload,
        });
      } catch (error) {
        useAlert(this.$t('REPORT.CSAT_REPORTS.DOWNLOAD_FAILED'));
      }
    },
    onPageNumberChange(pageIndex) {
      this.pageIndex = pageIndex;
      this.getResponses();
    },
    onFilterChange({
      from,
      to,
      selectedAgents,
      selectedInbox,
      selectedTeam,
      selectedRating,
    }) {
      // do not track filter change on initial load
      if (this.from !== 0 && this.to !== 0) {
        useTrack(REPORTS_EVENTS.FILTER_REPORT, {
          filterType: 'date',
          reportType: 'csat',
        });
      }

      this.from = from;
      this.to = to;
      this.userIds = selectedAgents.map(el => el.id);
      this.inbox = selectedInbox?.id;
      this.team = selectedTeam?.id;
      this.rating = selectedRating?.value;

      this.getAllData();
    },
  },
};
</script>

<template>
  <ReportHeader :header-title="$t('CSAT_REPORTS.HEADER')">
    <div class="flex items-center gap-2">
      <V4Button
        :label="$t('CSAT_SYNTHESIS.BUTTON')"
        icon="i-ph-sparkle"
        size="sm"
        color="slate"
        @click="openSynthesisDialog"
      />
      <V4Button
        :label="$t('CSAT_REPORTS.ALERT_SETTINGS.BUTTON')"
        icon="i-ph-gear"
        size="sm"
        color="slate"
        @click="openAlertSettings"
      />
      <V4Button
        :label="$t('CSAT_REPORTS.DOWNLOAD')"
        icon="i-ph-download-simple"
        size="sm"
        @click="downloadReports"
      />
    </div>
  </ReportHeader>

  <div class="flex flex-col gap-6">
    <CsatFilters
      :show-team-filter="isTeamsEnabled"
      @filter-change="onFilterChange"
    />
    <CsatMetrics :filters="requestPayload" />
    <CsatAgentRanking />
    <CsatTable :page-index="pageIndex" @page-change="onPageNumberChange" />
  </div>

  <CsatAlertSettingsDialog ref="alertSettingsDialog" />
  <CsatSynthesisDialog
    ref="csatSynthesisDialog"
    :initial-from="from"
    :initial-to="to"
    :is-synthesizing="isCsatSynthesisLoading"
    @synthesize="onCsatSynthesizeConfirm"
  />
</template>
